import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ENDPOINTS, canonicalUrl, resolveHtmlAssetUrls } from '../config';

/**
 * Loyalty Program — same pattern as KcbOffers / Policy pages:
 * - Page chrome uses theme tokens (light + dark)
 * - Body HTML comes from Policy Master (Jodit), slug: loyalty-program
 * - Seeded markup uses .lp-* classes (no hardcoded colors) so dark mode stays readable
 */
export default function LoyaltyProgram() {
    const [policy, setPolicy] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            try {
                const res = await fetch(ENDPOINTS.POLICIES.BY_SLUG('loyalty-program'));
                const data = await res.json();
                if (!cancelled && data.isOk && data.data && data.data.isActive !== false) {
                    setPolicy(data.data);
                }
            } catch (err) {
                console.error('Error loading loyalty program policy:', err);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, []);

    const metaTitle = policy?.metaTitle?.trim() || policy?.title?.trim() || 'Loyalty Program | Fone Express';
    const plainDescription = (policy?.description || '')
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    const metaDescription =
        policy?.metaDescription?.trim() ||
        plainDescription.slice(0, 160) ||
        'Earn and redeem Fone Express loyalty points on every purchase.';

    return (
        <div className="min-h-screen bg-muted py-12 px-4 sm:px-6 lg:px-8">
            <Helmet>
                <title>{metaTitle}</title>
                <link rel="canonical" href={canonicalUrl('/loyalty-program')} />
                {metaDescription ? <meta name="description" content={metaDescription} /> : null}
                {policy?.metaKeywords ? <meta name="keywords" content={policy.metaKeywords} /> : null}
            </Helmet>

            <div className="max-w-3xl mx-auto">
                <nav className="flex items-center gap-2 text-sm mb-8 text-muted-foreground">
                    <Link to="/" className="hover:text-primary transition-colors">Home</Link>
                    <i className="ri-arrow-right-s-line text-muted-foreground/60"></i>
                    <Link to="/account/loyalty" className="hover:text-primary transition-colors">Loyalty Wallet</Link>
                    <i className="ri-arrow-right-s-line text-muted-foreground/60"></i>
                    <span className="text-foreground font-medium">How it Works</span>
                </nav>

                <div className="bg-card rounded-2xl shadow-sm border border-border overflow-hidden">
                    {loading ? (
                        <div className="p-16 flex justify-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                        </div>
                    ) : policy?.description ? (
                        <>
                            <style>{`
                                .lp-content {
                                    --lp-navy: #1E2C4A;
                                    --lp-orange: #E94326;
                                    --lp-muted: #636E72;
                                    --lp-border: #E9ECEF;
                                    --lp-soft: #F8F9FA;
                                    --lp-hero: #FFF5F3;
                                    --lp-hero-border: #F1E5E2;
                                    --lp-earn-bg: #FFF5F3;
                                    --lp-earn-border: #F5C6BE;
                                    --lp-note-bg: #FFFBEB;
                                    --lp-note-border: #FDE68A;
                                    --lp-note-text: #92400E;
                                    --lp-icon-earn-bg: #DCFCE7;
                                    --lp-icon-redeem-bg: #DBEAFE;
                                    --lp-icon-time-bg: #FEF3C7;
                                    color: var(--lp-muted);
                                    line-height: 1.7;
                                }
                                .dark .lp-content {
                                    --lp-navy: oklch(var(--foreground));
                                    --lp-muted: oklch(var(--muted-foreground));
                                    --lp-border: oklch(var(--border));
                                    --lp-soft: oklch(var(--muted));
                                    --lp-hero: color-mix(in oklab, oklch(var(--primary)) 12%, oklch(var(--card)));
                                    --lp-hero-border: oklch(var(--border));
                                    --lp-earn-bg: color-mix(in oklab, oklch(var(--primary)) 14%, oklch(var(--card)));
                                    --lp-earn-border: color-mix(in oklab, oklch(var(--primary)) 35%, oklch(var(--border)));
                                    --lp-note-bg: color-mix(in oklab, #F59E0B 12%, oklch(var(--card)));
                                    --lp-note-border: color-mix(in oklab, #F59E0B 35%, oklch(var(--border)));
                                    --lp-note-text: oklch(var(--foreground));
                                    --lp-icon-earn-bg: color-mix(in oklab, #22C55E 18%, oklch(var(--card)));
                                    --lp-icon-redeem-bg: color-mix(in oklab, #3B82F6 18%, oklch(var(--card)));
                                    --lp-icon-time-bg: color-mix(in oklab, #F59E0B 18%, oklch(var(--card)));
                                }
                                .lp-content .lp-hero {
                                    text-align: center;
                                    padding: 2.5rem 1.5rem 2rem;
                                    background: var(--lp-hero);
                                    border-bottom: 1px solid var(--lp-hero-border);
                                }
                                .lp-content .lp-hero-icon {
                                    width: 4rem;
                                    height: 4rem;
                                    margin: 0 auto 1rem;
                                    display: flex;
                                    align-items: center;
                                    justify-content: center;
                                    background: color-mix(in oklab, oklch(var(--primary)) 18%, transparent);
                                    border-radius: 1rem;
                                    font-size: 1.75rem;
                                    color: oklch(var(--primary));
                                }
                                .lp-content .lp-hero h1 {
                                    margin: 0 0 0.5rem;
                                    font-size: 1.875rem;
                                    font-weight: 700;
                                    color: var(--lp-navy);
                                }
                                .lp-content .lp-hero p {
                                    margin: 0 auto;
                                    max-width: 28rem;
                                    color: var(--lp-muted);
                                }
                                .lp-content .lp-body {
                                    padding: 2.5rem 2rem;
                                }
                                .lp-content .lp-section {
                                    display: flex;
                                    gap: 1.5rem;
                                    align-items: flex-start;
                                    margin-bottom: 3rem;
                                }
                                .lp-content .lp-section:last-child {
                                    margin-bottom: 0;
                                }
                                .lp-content .lp-icon {
                                    width: 3rem;
                                    height: 3rem;
                                    flex-shrink: 0;
                                    border-radius: 0.75rem;
                                    display: flex;
                                    align-items: center;
                                    justify-content: center;
                                    font-size: 1.35rem;
                                }
                                .lp-content .lp-icon-earn { background: var(--lp-icon-earn-bg); color: #16A34A; }
                                .lp-content .lp-icon-redeem { background: var(--lp-icon-redeem-bg); color: #2563EB; }
                                .lp-content .lp-icon-time { background: var(--lp-icon-time-bg); color: #D97706; }
                                .dark .lp-content .lp-icon-earn { color: #4ADE80; }
                                .dark .lp-content .lp-icon-redeem { color: #60A5FA; }
                                .dark .lp-content .lp-icon-time { color: #FBBF24; }
                                .lp-content .lp-section h2 {
                                    margin: 0 0 0.75rem;
                                    font-size: 1.25rem;
                                    font-weight: 700;
                                    text-transform: none;
                                    letter-spacing: 0;
                                    border: none;
                                    padding: 0;
                                    color: var(--lp-navy);
                                }
                                .lp-content .lp-section p {
                                    margin: 0 0 1rem;
                                    color: var(--lp-muted);
                                }
                                .lp-content .lp-section strong {
                                    color: var(--lp-navy);
                                    font-weight: 600;
                                }
                                .lp-content .lp-stats {
                                    display: flex;
                                    flex-wrap: wrap;
                                    gap: 1rem;
                                }
                                .lp-content .lp-stat {
                                    flex: 1;
                                    min-width: 10rem;
                                    padding: 1rem;
                                    border-radius: 0.75rem;
                                    background: var(--lp-soft);
                                    border: 1px solid var(--lp-border);
                                }
                                .lp-content .lp-stat-earn {
                                    background: var(--lp-earn-bg);
                                    border-color: var(--lp-earn-border);
                                }
                                .lp-content .lp-stat-label {
                                    font-size: 0.7rem;
                                    font-weight: 700;
                                    text-transform: uppercase;
                                    letter-spacing: 0.04em;
                                    color: var(--lp-muted);
                                    margin-bottom: 0.25rem;
                                }
                                .lp-content .lp-stat-earn .lp-stat-label {
                                    color: var(--lp-orange);
                                }
                                .lp-content .lp-stat-value {
                                    font-size: 1.125rem;
                                    font-weight: 700;
                                    color: var(--lp-navy);
                                }
                                .lp-content .lp-stat-earn .lp-stat-value {
                                    color: var(--lp-orange);
                                }
                                .lp-content .lp-list {
                                    margin: 0;
                                    padding: 0;
                                    list-style: none;
                                }
                                .lp-content .lp-list li {
                                    margin-bottom: 0.5rem;
                                    color: var(--lp-muted);
                                    display: flex;
                                    align-items: flex-start;
                                    gap: 0.5rem;
                                }
                                .lp-content .lp-list li::before {
                                    content: "✓";
                                    color: #22C55E;
                                    font-weight: 700;
                                    flex-shrink: 0;
                                }
                                .lp-content .lp-divider {
                                    border: none;
                                    border-top: 1px solid var(--lp-border);
                                    margin: 0 0 3rem;
                                }
                                .lp-content .lp-note {
                                    padding: 1rem;
                                    border-radius: 0.75rem;
                                    background: var(--lp-note-bg);
                                    border: 1px solid var(--lp-note-border);
                                    color: var(--lp-note-text);
                                    font-size: 0.875rem;
                                }
                                .lp-content .lp-cta {
                                    text-align: center;
                                    padding: 2rem 1.5rem;
                                    background: var(--lp-soft);
                                    border-top: 1px solid var(--lp-border);
                                }
                                .lp-content .lp-cta h3 {
                                    margin: 0 0 1rem;
                                    color: var(--lp-navy);
                                    font-weight: 700;
                                }
                                .lp-content .lp-cta a.lp-btn {
                                    display: inline-flex;
                                    align-items: center;
                                    gap: 0.5rem;
                                    padding: 0.75rem 2rem;
                                    background: var(--lp-orange);
                                    color: #fff !important;
                                    border-radius: 0.5rem;
                                    font-weight: 600;
                                    text-decoration: none;
                                }
                                .lp-content .lp-cta a.lp-btn:hover {
                                    filter: brightness(1.08);
                                    text-decoration: none;
                                }
                                .lp-content a:not(.lp-btn) {
                                    color: var(--lp-orange);
                                }
                                @media (max-width: 640px) {
                                    .lp-content .lp-body { padding: 1.5rem 1.25rem; }
                                    .lp-content .lp-section { flex-direction: column; gap: 1rem; }
                                }
                            `}</style>
                            <div
                                className="lp-content"
                                dangerouslySetInnerHTML={{ __html: resolveHtmlAssetUrls(policy.description) }}
                            />
                        </>
                    ) : (
                        <div className="p-10 text-center">
                            <i className="ri-gift-line text-4xl text-muted-foreground mb-3" />
                            <h1 className="text-xl font-bold text-foreground mb-2">Loyalty Program</h1>
                            <p className="text-muted-foreground mb-4">
                                Content is managed in Admin → Policy Master (slug: <code>loyalty-program</code>).
                            </p>
                            <Link to="/products" className="text-primary font-medium hover:underline">
                                Continue shopping
                            </Link>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
